import {
	ClientEntityType,
	OrganizationEntityType,
	JobEntityType,
} from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { Currency, Region } from "../../types/enums";
import { RegionConfig } from "../../types/region.types";
import { useCentralStore } from "../../store/Central";

export const getCurrencyFromRegion = (
	client: ClientEntityType | null,
	organization: OrganizationEntityType,
	activatedRegions: RegionConfig[]
): string => {
	const regionId =
		client?.default_activated_region_id ??
		organization.default_activated_region_id;

	if (!regionId) {
		Logger.warn("No default region found, falling back to CHF");
		return Currency.CHF;
	}

	const region = activatedRegions.find((r) => r.id === regionId);

	if (!region || !region.currency_text) {
		Logger.warn("Region currency not found in store, falling back to CHF");
		return Currency.CHF;
	}

	return region.currency_text;
};

export const hidePaymentSlip = (
	client: ClientEntityType | null,
	organization: OrganizationEntityType,
	activatedRegions: RegionConfig[]
): boolean => {
	const regionId =
		client?.default_activated_region_id ??
		organization.default_activated_region_id;

	if (!regionId) {
		return false; //by default show payment slip
	}

	const region = activatedRegions.find((r) => r.id === regionId);

	if (!region) {
		return false; //by default show payment slip
	}

	return region.region != Region.CH;
};

/**
 * Gets currency following hierarchy: job → client → CHF
 * (simplified version without region lookup for PDF templates)
 */
export const getCurrencyHierarchy = (
	job: JobEntityType | null | undefined,
	client: ClientEntityType | null | undefined,
	organization?: OrganizationEntityType | null,
	activatedRegions?: RegionConfig[]
): string => {
	if (job?.currency_text) {
		return job.currency_text;
	}

	if (!client || !organization || !activatedRegions) {
		return Currency.CHF;
	}

	const currency = getCurrencyFromRegion(
		client,
		organization,
		activatedRegions
	);
	return currency;
};

/**
 * React hook version for use in components
 */
export const useCurrencyHierarchy = (
	job: JobEntityType | null | undefined,
	client: ClientEntityType | null | undefined
): string => {
	const activatedRegions = useCentralStore((state) => state.activatedRegions);
	const organization = useCentralStore((state) => state.organization);

	return getCurrencyHierarchy(job, client, organization, activatedRegions);
};

export const isRegion = (
	regionId: number,
	region: Region,
	activatedRegions: RegionConfig[]
) => {
	const foundRegion = activatedRegions.find((r) => r.id === regionId);

	return foundRegion?.region === region;
};

export const hasRegion = (region: Region, activatedRegions: RegionConfig[]) => {
	return activatedRegions.some((r) => r.region === region);
};

/**
 * Resolves the Region kind (CH/DE) for a document following the same hierarchy as
 * currency: client.default_activated_region_id → organization.default_activated_region_id.
 * Returns null when neither is set or the activated region can't be found.
 */
export const resolveRegionKind = (
	client: ClientEntityType | null | undefined,
	organization: OrganizationEntityType | null | undefined,
	activatedRegions: Pick<RegionConfig, "id" | "region">[]
): Region | null => {
	const regionId =
		client?.default_activated_region_id ??
		organization?.default_activated_region_id;

	if (!regionId) {
		return null;
	}

	return activatedRegions.find((r) => r.id === regionId)?.region ?? null;
};

/**
 * React hook version of resolveRegionKind for use in components.
 */
export const useRegionKind = (
	client: ClientEntityType | null | undefined
): Region | null => {
	const activatedRegions = useCentralStore((state) => state.activatedRegions);
	const organization = useCentralStore((state) => state.organization);

	return resolveRegionKind(client, organization, activatedRegions);
};
